import React, { useRef, useEffect } from "react";

const LoginBackground = ({ color, h }) => {
  const canvasRef = useRef(null);
  const w = parseInt(h, 10) * (179 / 64);

  // Canvas animation handling - updates when component mounts
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    const cellSize = 64; // Grid cell size matches the original CSS grid

    // Animation variables for the orbs
    let animationFrame;
    let time = 0;

    // Store the white blocks pattern so it doesn't change every frame
    let whiteBlocksPattern = null;

    // Define drawBackground function first before it's used
    function drawBackground() {
      // Clear canvas
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      // Draw grid pattern with gradient opacity
      drawGrid(ctx, cellSize, canvas.width, canvas.height);

      // Draw the orbs with animation
      drawOrbs(ctx, time, canvas.width, canvas.height);

      // Draw the white blocks pattern
      drawWhiteBlocks(ctx, cellSize, whiteBlocksPattern);

      // Update animation time
      time += 0.01;

      // Request next frame
      animationFrame = requestAnimationFrame(drawBackground);
    }

    // Set canvas dimensions to match window size - defined after drawBackground
    const resizeCanvas = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;

      // Don't regenerate pattern on resize - keep it static
    };

    // Add event listener for window resize
    window.addEventListener("resize", resizeCanvas);

    // Initial setup: set canvas size, generate pattern once, then start animation
    resizeCanvas();

    // Generate the white blocks pattern once based on initial window size
    const cols = Math.ceil(window.innerWidth / cellSize);
    const rows = Math.ceil(window.innerHeight / cellSize);
    whiteBlocksPattern = generateWhiteBlocksPattern(
      rows,
      cols,
      60,
      FillPattern.RANDOM
    );
    drawBackground();

    // Cleanup
    return () => {
      window.removeEventListener("resize", resizeCanvas);
      cancelAnimationFrame(animationFrame);
    };
  }, []);

  // Draw grid with gradient overlay and vignette effect
  const drawGrid = (ctx, cellSize, width, height) => {
    // Create gradient for the background
    const gradient = ctx.createLinearGradient(0, 0, width, height);
    gradient.addColorStop(0, "rgba(255, 255, 255, 1)");
    gradient.addColorStop(0.3, "rgba(255, 255, 255, 0)");
    gradient.addColorStop(0.7, "rgba(255, 255, 255, 0)");
    gradient.addColorStop(1, "rgba(255, 255, 255, 1)");

    // Fill background with gradient
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, width, height);

    // Calculate center and maximum distance for vignette effect
    const centerX = width / 2;
    const centerY = height / 2;
    const maxDistance = Math.sqrt(centerX * centerX + centerY * centerY);

    // Helper function to calculate opacity based on distance from center
    const getVignetteOpacity = (x, y) => {
      const distance = Math.sqrt((x - centerX) ** 2 + (y - centerY) ** 2);
      const normalizedDistance = distance / maxDistance;
      // Create a smooth falloff - stronger opacity in center, fading to edges
      return Math.max(0, 0.2 * (1 - Math.pow(normalizedDistance, 1.5)));
    };

    ctx.lineWidth = 1;

    // Draw vertical lines with vignette opacity
    for (let x = 0; x <= width; x += cellSize) {
      // Sample opacity at multiple points along the line for smooth gradients
      const gradient = ctx.createLinearGradient(x, 0, x, height);

      // Add gradient stops at regular intervals
      for (let y = 0; y <= height; y += height / 20) {
        const opacity = getVignetteOpacity(x, y);
        const stop = y / height;
        gradient.addColorStop(stop, `rgba(200, 200, 200, ${opacity})`);
      }

      ctx.strokeStyle = gradient;
      ctx.beginPath();
      ctx.moveTo(x, 0);
      ctx.lineTo(x, height);
      ctx.stroke();
    }

    // Draw horizontal lines with vignette opacity
    for (let y = 0; y <= height; y += cellSize) {
      // Sample opacity at multiple points along the line for smooth gradients
      const gradient = ctx.createLinearGradient(0, y, width, y);

      // Add gradient stops at regular intervals
      for (let x = 0; x <= width; x += width / 20) {
        const opacity = getVignetteOpacity(x, y);
        const stop = x / width;
        gradient.addColorStop(stop, `rgba(200, 200, 200, ${opacity})`);
      }

      ctx.strokeStyle = gradient;
      ctx.beginPath();
      ctx.moveTo(0, y);
      ctx.lineTo(width, y);
      ctx.stroke();
    }
  };

  // Draw animated orbs
  const drawOrbs = (ctx, time, width, height) => {
    const centerX = width / 2;
    const centerY = height / 2;
    const orbSize = 1300;

    // Save context for clipping
    ctx.save();

    // Define orb configurations
    const orbs = [
      {
        // Left orb config
        position: {
          xOffset: -0.4, // Left of center
          yOffset: 0.35, // Below center
        },
        animation: {
          scale: {
            base: 0.92,
            amplitude: 0.1,
            phase: 0,
          },
          opacity: {
            base: 0.65,
            amplitude: 0.175,
            phase: 0,
          },
        },
        gradient: {
          stops: [
            { position: 0, opacityFactor: 0.9 },
            { position: 0.1, opacityFactor: 0.7 },
            { position: 0.3, opacityFactor: 0.3 },
            { position: 0.4, opacityFactor: 0.1 },
            { position: 0.5, opacityFactor: 0 },
          ],
        },
      },
      {
        // Right orb config
        position: {
          xOffset: 0.4, // Right of center
          yOffset: -0.6, // Above center
        },
        animation: {
          scale: {
            base: 0.9,
            amplitude: 0.125,
            phase: 1,
          },
          opacity: {
            base: 0.6,
            amplitude: 0.15,
            phase: 1,
          },
        },
        gradient: {
          stops: [
            { position: 0, opacityFactor: 0.85 },
            { position: 0.1, opacityFactor: 0.65 },
            { position: 0.3, opacityFactor: 0.3 },
            { position: 0.4, opacityFactor: 0.12 },
            { position: 0.5, opacityFactor: 0 },
          ],
        },
      },
    ];

    // Draw each orb based on its configuration
    orbs.forEach((orb) => {
      // Calculate position
      const x = centerX + centerX * orb.position.xOffset;
      const y = centerY + centerY * orb.position.yOffset;

      // Calculate animation values
      const animPhase = time + orb.animation.scale.phase;
      const scale =
        orb.animation.scale.base +
        Math.sin(animPhase) * orb.animation.scale.amplitude;
      const opacity =
        orb.animation.opacity.base +
        Math.sin(animPhase) * orb.animation.opacity.amplitude;

      // Create gradient
      const gradient = ctx.createRadialGradient(
        x,
        y,
        0,
        x,
        y,
        (orbSize * scale) / 2
      );

      // Add gradient stops
      orb.gradient.stops.forEach((stop) => {
        const stopOpacity =
          stop.opacityFactor === 0 ? 0 : opacity * stop.opacityFactor;
        gradient.addColorStop(
          stop.position,
          `rgba(52, 211, 153, ${stopOpacity})`
        );
      });

      // Draw the orb
      ctx.fillStyle = gradient;
      ctx.beginPath();
      ctx.arc(x, y, (orbSize * scale) / 2, 0, Math.PI * 2);
      ctx.fill();
    });

    ctx.restore();
  };

  // Add this enum at the top of the file
  const FillPattern = {
    ORGANIC: "organic",
    RANDOM: "random",
  };

  // Replace the drawWhiteBlocks function with this new implementation
  const drawWhiteBlocks = (ctx, cellSize, pattern) => {
    if (!pattern) return;

    const canvasWidth = ctx.canvas.width;
    const canvasHeight = ctx.canvas.height;
    const cols = Math.ceil(canvasWidth / cellSize);
    const rows = Math.ceil(canvasHeight / cellSize);

    // Draw the pre-generated pattern
    ctx.fillStyle = "rgba(255, 255, 255, 0.3)";

    for (let row = 0; row < rows && row < pattern.length; row++) {
      for (let col = 0; col < cols && col < pattern[row].length; col++) {
        if (pattern[row][col]) {
          const x = col * cellSize + 2;
          const y = row * cellSize + 2;
          const width = cellSize - 4;
          const height = cellSize - 4;
          const radius = 4;

          ctx.beginPath();
          ctx.moveTo(x + radius, y);
          ctx.arcTo(x + width, y, x + width, y + height, radius);
          ctx.arcTo(x + width, y + height, x, y + height, radius);
          ctx.arcTo(x, y + height, x, y, radius);
          ctx.arcTo(x, y, x + width, y, radius);
          ctx.closePath();
          ctx.fill();
        }
      }
    }
  };

  // Add this new function for generating the white blocks pattern
  const generateWhiteBlocksPattern = (
    rows,
    cols,
    fillPercentage = 20,
    pattern = FillPattern.ORGANIC
  ) => {
    let grid = [];

    // Initialize empty grid
    for (let row = 0; row < rows; row++) {
      grid[row] = new Array(cols).fill(false);
    }

    if (pattern === FillPattern.ORGANIC) {
      grid = generateOrganicPattern(grid, rows, cols, fillPercentage);
    } else if (pattern === FillPattern.RANDOM) {
      grid = generateRandomPattern(grid, rows, cols, fillPercentage);
    }
    // Future patterns can be added here

    return grid;
  };

  // Add this new function for organic pattern generation
  const generateOrganicPattern = (grid, rows, cols, fillPercentage) => {
    const targetCells = Math.floor((rows * cols * fillPercentage) / 100);
    let filledCells = 0;

    // Number of seed points based on grid size (creates natural cluster count)
    const numSeeds = Math.max(3, Math.floor(Math.sqrt(rows * cols) / 8));

    // Generate random seed points
    const seeds = [];
    for (let i = 0; i < numSeeds; i++) {
      seeds.push({
        row: Math.floor(Math.random() * rows),
        col: Math.floor(Math.random() * cols),
        strength: 0.3 + Math.random() * 0.7, // Random strength between 0.3-1.0
      });
    }

    // Grow clusters from seeds using probability-based expansion
    const queue = [...seeds.map((seed) => ({ ...seed, distance: 0 }))];

    while (queue.length > 0 && filledCells < targetCells) {
      // Sort queue by distance to process closer cells first (creates more cohesive clusters)
      queue.sort((a, b) => a.distance - b.distance);

      const current = queue.shift();
      const { row, col, strength, distance } = current;

      // Skip if out of bounds or already filled
      if (row < 0 || row >= rows || col < 0 || col >= cols || grid[row][col]) {
        continue;
      }

      // Calculate probability of filling based on distance and seed strength
      // Probability decreases with distance but is influenced by seed strength
      const distanceDecay = Math.exp(-distance * 0.3);
      const probability = strength * distanceDecay;

      // Additional randomness for organic feel
      const randomFactor = 0.7 + Math.random() * 0.3;
      const finalProbability = probability * randomFactor;

      // Fill cell if probability check passes
      if (Math.random() < finalProbability) {
        grid[row][col] = true;
        filledCells++;

        // Add neighboring cells to queue with increased distance
        const neighbors = [
          { row: row - 1, col: col }, // Up
          { row: row + 1, col: col }, // Down
          { row: row, col: col - 1 }, // Left
          { row: row, col: col + 1 }, // Right
          { row: row - 1, col: col - 1 }, // Up-left
          { row: row - 1, col: col + 1 }, // Up-right
          { row: row + 1, col: col - 1 }, // Down-left
          { row: row + 1, col: col + 1 }, // Down-right
        ];

        neighbors.forEach((neighbor) => {
          if (
            neighbor.row >= 0 &&
            neighbor.row < rows &&
            neighbor.col >= 0 &&
            neighbor.col < cols &&
            !grid[neighbor.row][neighbor.col]
          ) {
            // Slight preference for cardinal directions over diagonals for more natural growth
            const isDiagonal = neighbor.row !== row && neighbor.col !== col;
            const distanceIncrement = isDiagonal ? 1.4 : 1.0;

            queue.push({
              row: neighbor.row,
              col: neighbor.col,
              strength: strength * 0.95, // Slight strength decay as we move away from seed
              distance: distance + distanceIncrement,
            });
          }
        });
      }
    }

    return grid;
  };

  // Add this new function for random pattern generation
  const generateRandomPattern = (grid, rows, cols, fillPercentage) => {
    const targetCells = Math.floor((rows * cols * fillPercentage) / 100);
    let filledCells = 0;

    // Create array of all possible cell positions
    const allPositions = [];
    for (let row = 0; row < rows; row++) {
      for (let col = 0; col < cols; col++) {
        allPositions.push({ row, col });
      }
    }

    // Shuffle the array using Fisher-Yates algorithm for true randomness
    for (let i = allPositions.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [allPositions[i], allPositions[j]] = [allPositions[j], allPositions[i]];
    }

    // Fill the first targetCells positions from the shuffled array
    for (let i = 0; i < targetCells && i < allPositions.length; i++) {
      const { row, col } = allPositions[i];
      grid[row][col] = true;
      filledCells++;
    }

    return grid;
  };

  return (
    <div className="container w-screen max-w-none">
      {/* Canvas element replaces all three layers */}
      <canvas ref={canvasRef} className="absolute inset-0 z-[-99]" />
    </div>
  );
};

export default LoginBackground;
